import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createBreadcrumbStructuredData, createFAQStructuredData } from '../../utils/structuredData';

export default function TradingHoursPage() {
  // SEO設定
  usePageSEO({
    title: 'DMM FXの取引時間完全ガイド｜24時間取引とおすすめ時間帯',
    description: 'DMM FXの取引時間を徹底解説。月曜7:00〜土曜6:50の24時間取引、世界の主要市場の特徴、トレードスタイル別のおすすめ時間帯、経済指標カレンダーの使い方を詳しく説明します。',
    keywords: 'DMM FX, 取引時間, 24時間, 市場, おすすめ時間帯, 経済指標',
    ogImage: 'https://public.readdy.ai/ai/img_res/59441aa864c481af35ec0d5ac0e5cfa7.jpg',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FXの取引時間完全ガイド',
        description: 'DMM FXの取引可能時間、世界の主要市場の特徴、トレードスタイル別のおすすめ時間帯を詳しく解説。経済指標カレンダーの活用方法も紹介します。',
        image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20trading%20hours&width=1200&height=630&seq=hours-article&orientation=landscape',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        url: '/trading-hours'
      }),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: '取引時間', url: '/trading-hours' }
      ]),
      createFAQStructuredData([
        {
          question: 'DMM FXの取引時間は何時から何時までですか？',
          answer: 'DMM FXの取引時間は、冬時間（11月第1日曜〜3月第2日曜）は月曜7:00〜土曜6:50、夏時間（3月第2日曜〜11月第1日曜）は月曜7:00〜土曜5:50です。平日であれば原則24時間いつでも取引できます。土日は世界の為替市場が休場となるため取引できません。'
        },
        {
          question: 'メンテナンス時間はいつですか？',
          answer: 'DMM FXのメンテナンス時間は、冬時間は毎日6:50〜7:10（20分間）、夏時間は毎日5:50〜6:10（20分間）です。メンテナンス中はログインや注文ができませんので、ご注意ください。'
        },
        {
          question: '取引におすすめの時間帯はいつですか？',
          answer: 'おすすめの時間帯は取引スタイルによって異なります。スキャルピングは22:00〜翌2:00（ロンドン×ニューヨーク市場の重なり）、デイトレードは17:00〜翌2:00（ロンドン市場開始からニューヨーク市場前半）、会社員の方は20:00〜24:00が取引しやすい時間帯です。この時間帯は取引量が多く、値動きが活発になります。'
        },
        {
          question: '日本の祝日でも取引できますか？',
          answer: 'はい、日本の祝日でも月〜金であれば原則として通常通り取引可能です。ただし、クリスマスや年末年始は欧米市場の休場に伴い、取引時間が短縮または休場となる場合があります。最新の営業カレンダーはDMM FX公式サイトでご確認ください。'
        },
        {
          question: '世界の主要市場の取引時間を教えてください。',
          answer: '主要市場の取引時間（日本時間）は以下の通りです。東京市場：9:00〜17:00、ロンドン市場：17:00〜翌2:00、ニューヨーク市場：22:00〜翌6:00、シドニー市場：7:00〜15:00。特にロンドン市場とニューヨーク市場が重なる22:00〜翌2:00は、最も取引量が多く値動きが活発な時間帯です。'
        }
      ])
    ]
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX基礎知識', url: '/fx-basic' },
            { name: '取引時間', url: '/trading-hours' }
          ]} />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">DMM FXの取引時間</h1>
            <p className="text-xl text-teal-50 mb-8 max-w-3xl mx-auto">
              取引可能時間からおすすめの時間帯まで詳しく解説
            </p>
            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              DMM FXで始める
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
        </div>
      </section>

      {/* 取引時間 Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">DMM FXの取引時間</h2>
            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-8 mb-6">
              <div className="text-center mb-6">
                <p className="text-lg text-gray-700 mb-2">取引可能時間</p>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">冬時間（11月第1日曜〜3月第2日曜）</p>
                    <p className="text-3xl font-bold text-teal-600"><span className="bg-red-100 text-red-700 px-3 py-1 rounded">月曜7:00 〜 土曜6:50</span></p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">夏時間（3月第2日曜〜11月第1日曜）</p>
                    <p className="text-3xl font-bold text-teal-600"><span className="bg-red-100 text-red-700 px-3 py-1 rounded">月曜7:00 〜 土曜5:50</span></p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start bg-gray-50 rounded-lg p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">原則24時間取引可能</h3>
                  <p className="text-gray-700 leading-relaxed">
                    土日を除く平日であれば、原則24時間いつでも取引できます。仕事終わりの夜間や早朝でも自由に取引可能です。
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-gray-50 rounded-lg p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-calendar-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">土日は取引不可</h3>
                  <p className="text-gray-700 leading-relaxed">
                    土曜日の早朝から月曜日の朝まで、世界の為替市場が休場となるため取引できません。
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-gray-50 rounded-lg p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-refresh-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2"><span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">メンテナンス時間</span></h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>冬時間：</strong><span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">毎日6:50〜7:10（20分間）</span><br />
                    <strong>夏時間：</strong><span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">毎日5:50〜6:10（20分間）</span><br />
                    メンテナンス中はログインや注文ができません。
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <i className="ri-information-line text-2xl text-amber-600 mr-4 flex-shrink-0 mt-1"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">夏時間について</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  米国の夏時間（サマータイム）は3月第2日曜から11月第1日曜まで適用されます。夏時間中は取引終了時間が1時間早まります。
                </p>
                <div className="bg-white rounded-lg p-4 space-y-2 text-sm">
                  <p className="text-gray-700"><strong>2025年の切り替え日：</strong></p>
                  <p className="text-gray-700">• 夏時間開始：2025年3月9日（日）</p>
                  <p className="text-gray-700">• 冬時間開始：2025年11月2日（日）</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6">
            <div className="flex items-start">
              <i className="ri-alert-line text-2xl text-red-600 mr-4 flex-shrink-0 mt-1"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">年末年始・祝日の取引時間</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>日本の祝日：</strong>月〜金の祝日は原則として通常通り取引可能です。<br />
                  <strong>クリスマス・年末年始：</strong>欧米市場の休場に伴い、取引時間が短縮または休場となる場合があります。
                </p>
                <p className="text-sm text-gray-600">
                  ※最新の営業カレンダーはDMM FX公式サイトでご確認ください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 市場別取引時間 Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">世界の主要市場と取引時間</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">東京市場</h3>
                  <p className="text-sm text-gray-600">アジア時間</p>
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <p className="font-bold text-gray-900 mb-2">取引時間: 9:00〜17:00</p>
                <p className="text-sm text-gray-700">日本時間の午前中が中心</p>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <p>• 円が絡む通貨ペアが活発</p>
                <p>• 比較的値動きは穏やか</p>
                <p>• 日本の経済指標発表に注目</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">ロンドン市場</h3>
                  <p className="text-sm text-gray-600">欧州時間</p>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 mb-4">
                <p className="font-bold text-gray-900 mb-2">取引時間: 17:00〜翌2:00</p>
                <p className="text-sm text-gray-700">日本時間の夕方から深夜</p>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <p>• 世界最大の取引量</p>
                <p>• ユーロ・ポンドが活発</p>
                <p>• 値動きが大きくなりやすい</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">ニューヨーク市場</h3>
                  <p className="text-sm text-gray-600">米国時間</p>
                </div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 mb-4">
                <p className="font-bold text-gray-900 mb-2">取引時間: 22:00〜翌6:00</p>
                <p className="text-sm text-gray-700">日本時間の夜から早朝</p>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <p>• 米ドルが中心</p>
                <p>• 重要な経済指標が多い</p>
                <p>• ロンドンと重なる時間帯が活発</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">シドニー市場</h3>
                  <p className="text-sm text-gray-600">オセアニア時間</p>
                </div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 mb-4">
                <p className="font-bold text-gray-900 mb-2">取引時間: 7:00〜15:00</p>
                <p className="text-sm text-gray-700">日本時間の朝から午後</p>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <p>• 週の始まりの市場</p>
                <p>• 豪ドル・NZドルが中心</p>
                <p>• 取引量は比較的少ない</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">市場の重なり時間帯</h3>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg p-6">
                <p className="font-bold text-gray-900 mb-2">🔥 ロンドン × ニューヨーク（<span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">22:00〜翌2:00</span>）</p>
                <p className="text-gray-700">
                  最も取引量が多く、値動きが活発な時間帯。大きな利益を狙えるチャンスですが、リスクも高まります。
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="font-bold text-gray-900 mb-2">東京 × ロンドン（15:00〜17:00）</p>
                <p className="text-gray-700">
                  東京市場の終わりとロンドン市場の始まりが重なる時間帯。徐々に値動きが活発になります。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* おすすめ時間帯 Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">トレードスタイル別おすすめ時間帯</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                  <i className="ri-flashlight-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">スキャルピング</h3>
                  <p className="text-gray-700 mb-4">数秒〜数分の超短期取引</p>
                  <div className="bg-teal-50 rounded-lg p-6">
                    <p className="font-semibold text-teal-900 mb-3">おすすめ時間: <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">22:00〜翌2:00</span></p>
                    <p className="text-gray-700 mb-4">
                      ロンドン市場とニューヨーク市場が重なる時間帯。取引量が多く、スプレッドも狭いため、短期売買に最適です。
                    </p>
                    <div className="space-y-2 text-sm text-gray-700">
                      <p>✓ 値動きが活発で利益を狙いやすい</p>
                      <p>✓ 流動性が高くスリッページが少ない</p>
                      <p>✓ 経済指標発表時は特にチャンス</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">デイトレード</h3>
                  <p className="text-gray-700 mb-4">数時間〜1日以内の取引</p>
                  <div className="bg-teal-50 rounded-lg p-6">
                    <p className="font-semibold text-teal-900 mb-3">おすすめ時間: <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">17:00〜翌2:00</span></p>
                    <p className="text-gray-700 mb-4">
                      ロンドン市場の開始からニューヨーク市場の前半まで。トレンドが形成されやすく、デイトレードに適しています。
                    </p>
                    <div className="space-y-2 text-sm text-gray-700">
                      <p>✓ 明確なトレンドが出やすい</p>
                      <p>✓ 仕事終わりでも取引可能</p>
                      <p>✓ 日をまたがずポジション決済できる</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                  <i className="ri-calendar-check-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">スイングトレード</h3>
                  <p className="text-gray-700 mb-4">数日〜数週間の中期取引</p>
                  <div className="bg-teal-50 rounded-lg p-6">
                    <p className="font-semibold text-teal-900 mb-3">おすすめ時間: いつでもOK</p>
                    <p className="text-gray-700 mb-4">
                      時間帯にこだわる必要はありませんが、エントリー・決済は値動きが活発な時間帯がおすすめです。
                    </p>
                    <div className="space-y-2 text-sm text-gray-700">
                      <p>✓ 時間に縛られない</p>
                      <p>✓ 大きなトレンドを狙える</p>
                      <p>✓ スワップポイントも獲得可能</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                  <i className="ri-briefcase-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">会社員の方</h3>
                  <p className="text-gray-700 mb-4">仕事終わりに取引したい方</p>
                  <div className="bg-teal-50 rounded-lg p-6">
                    <p className="font-semibold text-teal-900 mb-3">おすすめ時間: <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">20:00〜24:00</span></p>
                    <p className="text-gray-700 mb-4">
                      帰宅後の時間帯でも、ロンドン市場とニューヨーク市場が開いているため、十分に取引できます。
                    </p>
                    <div className="space-y-2 text-sm text-gray-700">
                      <p>✓ 仕事と両立しやすい</p>
                      <p>✓ 値動きが活発な時間帯</p>
                      <p>✓ 無理なく続けられる</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 利用者の声へのリンク */}
          <div className="mt-12 bg-white rounded-xl p-6 border-l-4 border-teal-500 shadow-md">
            <p className="text-gray-700 leading-relaxed">
              実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
              → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* 経済指標カレンダー Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-calendar-schedule-line text-2xl text-white"></i>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">経済指標カレンダー</h2>
                <p className="text-gray-600 mt-1">重要な経済指標の発表スケジュールを事前にチェック</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                FX取引では、<strong>経済指標の発表タイミング</strong>を把握することが非常に重要です。米国雇用統計やFOMC政策金利発表などの重要指標は、相場を大きく動かす要因になります。
              </p>
              <p className="text-gray-700 leading-relaxed">
                以下の経済指標カレンダーを活用して、発表スケジュールを事前に確認しましょう。
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-gray-50 rounded-lg p-5">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-star-fill text-sm text-red-500"></i>
                  </div>
                  <h4 className="font-bold text-gray-900">特に注目すべき指標</h4>
                </div>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center"><span className="w-2 h-2 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>米国雇用統計（毎月第1金曜）</li>
                  <li className="flex items-center"><span className="w-2 h-2 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>FOMC政策金利発表</li>
                  <li className="flex items-center"><span className="w-2 h-2 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>米国CPI（消費者物価指数）</li>
                  <li className="flex items-center"><span className="w-2 h-2 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>日銀金融政策決定会合</li>
                  <li className="flex items-center"><span className="w-2 h-2 bg-red-400 rounded-full mr-2 flex-shrink-0"></span>ECB政策金利発表</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-5">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-lightbulb-line text-sm text-amber-600"></i>
                  </div>
                  <h4 className="font-bold text-gray-900">初心者向けアドバイス</h4>
                </div>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center"><span className="w-2 h-2 bg-teal-400 rounded-full mr-2 flex-shrink-0"></span>重要指標の前後30分は取引を控える</li>
                  <li className="flex items-center"><span className="w-2 h-2 bg-teal-400 rounded-full mr-2 flex-shrink-0"></span>発表結果と予想値の差に注目する</li>
                  <li className="flex items-center"><span className="w-2 h-2 bg-teal-400 rounded-full mr-2 flex-shrink-0"></span>週初めにその週の指標を確認する習慣を</li>
                  <li className="flex items-center"><span className="w-2 h-2 bg-teal-400 rounded-full mr-2 flex-shrink-0"></span>損切り注文を必ず設定しておく</li>
                  <li className="flex items-center"><span className="w-2 h-2 bg-teal-400 rounded-full mr-2 flex-shrink-0"></span>慣れるまでは指標発表を「見る」だけでもOK</li>
                </ul>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <a 
                href="https://jp.investing.com/economic-calendar/" 
                target="_blank" 
                rel="nofollow noopener noreferrer"
                className="flex items-center justify-between bg-white border-2 border-teal-200 hover:border-teal-400 rounded-xl p-5 transition-all hover:shadow-md cursor-pointer group"
              >
                <div>
                  <p className="font-bold text-gray-900 group-hover:text-teal-600 transition-colors">Investinging.com</p>
                  <p className="text-xs text-gray-500 mt-1">経済指標カレンダー</p>
                </div>
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-external-link-line text-lg text-teal-500"></i>
                </div>
              </a>
            </div>

            {/* 経済指標カレンダーへのリンク */}
            <div className="mt-8 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-calendar-2-line text-teal-600 mr-2"></i>
                経済指標カレンダーをチェック
              </h4>
              <p className="text-gray-700 text-sm mb-4">
                取引前に必ず経済指標の発表スケジュールを確認しましょう。
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://jp.investing.com/economic-calendar/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line mr-2"></i>
                  Investing.com カレンダー
                </a>
                <Link 
                  to="/economic-indicators"
                  className="inline-flex items-center justify-center bg-white text-teal-600 border-2 border-teal-500 px-6 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-book-read-line mr-2"></i>
                  経済指標の見方を学ぶ
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 注意点 Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">取引時間の注意点</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">経済指標発表時</h3>
              <p className="text-gray-700 leading-relaxed">
                重要な経済指標発表時は、相場が急変動することがあります。初心者の方は、発表前後の取引は避けるのが無難です。
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-moon-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">深夜・早朝の取引</h3>
              <p className="text-gray-700 leading-relaxed">
                流動性が低い時間帯は、スプレッドが広がることがあります。また、突発的な値動きにも注意が必要です。
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-event-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">祝日・年末年始</h3>
              <p className="text-gray-700 leading-relaxed">
                各国の祝日や年末年始は、取引時間が変更されたり、流動性が低下することがあります。事前に確認しましょう。
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">週明け・週末</h3>
              <p className="text-gray-700 leading-relaxed">
                週明けの窓開けや、週末のポジション持ち越しにはリスクがあります。特に初心者の方は注意が必要です。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl shadow-xl p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">DMM FXで24時間取引を始めよう</h2>
            <p className="text-xl text-teal-50 mb-8">
              あなたのライフスタイルに合わせて自由に取引
            </p>

            {/* 口座開設手順へのリンク */}
            <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-6 max-w-2xl mx-auto">
              <p className="text-white leading-relaxed">
                口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
                → <Link to="/account-opening" className="text-teal-200 font-semibold hover:text-teal-100 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
              </p>
            </div>

            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-white text-teal-600 px-10 py-4 rounded-lg text-lg font-bold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              無料で口座開設する
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
        </div>
      </section>

      {/* 関連記事 Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">関連記事</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/economic-indicators" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-bar-chart-grouped-line text-white text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">経済指標の見方・活用方法</h3>
              <p className="text-sm text-gray-600 mb-4">FX取引で重要な経済指標の活用方法</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
            <Link to="/app-guide" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-smartphone-line text-white text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">アプリの使い方ガイド</h3>
              <p className="text-sm text-gray-600 mb-4">DMM FXアプリの機能と使い方を詳しく解説</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
            <Link to="/fx-basics" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-white text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">FXとは？初心者向け超入門</h3>
              <p className="text-sm text-gray-600 mb-4">FXの基本から仕組みまで分かりやすく解説</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
